import clsx from 'clsx';
import Link from 'next/link';
import React from 'react';

import Checkbox from '@/components/global/Checkbox';
import CustomFont from '@/components/global/CustomFont';
import FontLoader from '@/components/global/FontLoader';
import { useRegions } from '@/components/providers/RegionsProvider';
import FontStamp, {
  productStampAttribute,
  productStampColourScheme
} from '@/components/typeface/TypefaceCard/FontStamp';
import checkoutDataLayer from '@/lib/datalayer/checkout';
import { fontIsPro } from '@/lib/fonts/fontVersion';
import { generateS3PathFromVariants } from '@/lib/fonts/s3FontPath';
import { usePaths } from '@/lib/paths';
import {
  getFontDisplayName,
  getMetaDataValueByKey,
  getProductFontSizeAdjust,
  isFamilyVariant,
  isVariableFont
} from '@/lib/productHelpers';
import { translate } from '@/lib/translations';
import { SelectedAttribute, TypefaceCardFragment } from '@/saleor/api';

import styles from './TypefaceCard.module.scss';

export interface ITypefaceCardProps {
  product: TypefaceCardFragment;
  customText?: string;
  toggleCompareProduct?: Function;
  checked?: boolean;
}

const TypefaceCard = ({
  product,
  customText,
  toggleCompareProduct,
  checked
}: ITypefaceCardProps) => {
  const paths = usePaths();
  const { formatPrice } = useRegions();

  const { productType, attributes } = product;
  const { slug: productTypeSlug } = productType;
  const fontProduct = productTypeSlug === 'font-family';
  const fontDisplayName = getFontDisplayName({ name: product.name, metadata: product.metadata });
  const fontSizeAdjust = getProductFontSizeAdjust(product);

  let priceDisplay = formatPrice(product.pricing?.priceRange?.start?.net);
  if (
    product.pricing?.priceRange?.start?.net.amount !== product.pricing?.priceRange?.stop?.net.amount
  ) {
    priceDisplay = 'from ' + priceDisplay;
  }
  const imageStyle: React.CSSProperties = {};
  if (!!product.thumbnail?.url) {
    imageStyle.backgroundImage = `url(${product.thumbnail?.url})`;
    imageStyle.backgroundSize = 'auto';
    imageStyle.backgroundRepeat = 'no-repeat';
    imageStyle.backgroundPosition = 'center';
  }

  const productMetaData = getMetaDataValueByKey({ item: product });
  const styleCountMeta = productMetaData('styleCount');
  const weightCountMeta = productMetaData('weightCount');

  const stylesPro = product.variants.filter(
    (variant) => !isFamilyVariant(variant) && !isVariableFont(variant) && fontIsPro(variant)
  );
  const stylesStd = product.variants.filter(
    (variant) => !isFamilyVariant(variant) && !isVariableFont(variant) && !fontIsPro(variant)
  );
  const stylesProOrStd = stylesPro.length >= stylesStd.length ? stylesPro : stylesStd;
  const styleCount = styleCountMeta || stylesProOrStd.length;

  const productStampAttributeValue = productStampAttribute(attributes as Array<SelectedAttribute>);
  const productStampColours = productStampColourScheme(attributes as Array<SelectedAttribute>);
  const style = {
    '--stamp-bg': productStampColours[0] || null,
    '--stamp-fg': productStampColours[1] || null
  } as React.CSSProperties;

  return (
    <div
      style={productStampColours.length === 2 ? style : {}}
      className={clsx(styles.TypefaceCardContainer)}
    >
      {fontProduct && (
        <div className={styles.compare}>
          <Checkbox
            onChange={() => {
              toggleCompareProduct(product.id);
            }}
            isChecked={checked}
            name="compare"
            id={product.id}
            value={product.id}
            label="Compare"
            hideLabel
          />
        </div>
      )}
      <Link href={paths.typefaces._slug(product.slug).$url()} prefetch={false}>
        <a
          // add 'select_item' to the dataLayer
          onClick={() => {
            checkoutDataLayer.selectItem(product);
          }}
          className={styles.TypefaceCard}
        >
          {!fontProduct && (
            <div style={imageStyle}>
              {!!product.pricing?.onSale && (
                <>
                  <br />
                  <div>Sale</div>
                </>
              )}
            </div>
          )}
          <div>
            {!fontProduct && <p>{fontDisplayName}</p>}
            {fontProduct && (
              <div
                className={clsx(
                  styles.TypefaceCard__Details,
                  styles['TypefaceCard__Details--single']
                )}
              >
                <div>{translate(product, 'name')}</div>
              </div>
            )}
            {fontProduct && (
              <>
                <FontLoader
                  fullPath
                  fontDisplay="fallback"
                  fontName={product.name}
                  s3Path={generateS3PathFromVariants(product.slug, product.variants, attributes)}
                />
                <CustomFont fontName={product.name} uuid={product.id.replace('=', '')}>
                  <h4
                    className={styles.TypefaceCard__FontTitle}
                    style={
                      {
                        ...(fontSizeAdjust && { '--font-size-adjust': fontSizeAdjust })
                      } as React.CSSProperties
                    }
                  >
                    {customText ? customText : fontDisplayName.replace(/<br\s*\/?>/gi, '\n')}
                  </h4>
                </CustomFont>
              </>
            )}
            {fontProduct && (
              <div
                className={clsx(styles.TypefaceCard__Details, {
                  [`${styles['TypefaceCard__Details--multi']}`]: weightCountMeta !== undefined,
                  [`${styles['TypefaceCard__Details--single']}`]: weightCountMeta === undefined
                })}
              >
                <FontStamp
                  productStampAttributeValue={productStampAttributeValue}
                  styles={styles}
                />
                {weightCountMeta && <div>{`${weightCountMeta} weights`}</div>}
                <div>{`${styleCount} styles`}</div>
              </div>
            )}
            {!fontProduct && !!product.category && (
              <>
                <p>{translate(product.category, 'name')}</p>
                <p>{priceDisplay}</p>
              </>
            )}
          </div>
        </a>
      </Link>
    </div>
  );
};

export default TypefaceCard;
